import * as fn from '../../../helper/uiFunctions';


const isPlainObject = (value) => value && typeof value === "object" && !Array.isArray(value);

const mergeChartOptions = (baseOptions, overrideOptions = {}) => {
    const mergedOptions = { ...baseOptions };

    Object.entries(overrideOptions).forEach(([key, value]) => {
        if (isPlainObject(value) && isPlainObject(mergedOptions[key])) {
            mergedOptions[key] = mergeChartOptions(mergedOptions[key], value);
            return;
        }

        mergedOptions[key] = value;
    });

    return mergedOptions;
};

const buildFormatterMap = (formatters = []) => {
    return formatters.reduce((formatterMap, formatter) => {
        const columnIndex = Number(formatter.column);

        if (!Number.isInteger(columnIndex)) {
            return formatterMap;
        }

        if (!formatterMap[columnIndex]) {
            formatterMap[columnIndex] = [];
        }

        formatterMap[columnIndex].push(formatter);
        return formatterMap;
    }, {});
};

const formatNumberValue = (value, formatterOptions = {}) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    if (typeof value !== "number") {
        return String(value);
    }

    const {
        fractionDigits,
        groupingSymbol,
        negativeParens,
        prefix = "",
        suffix = "",
    } = formatterOptions;

    const useGrouping = groupingSymbol !== "";
    const minimumFractionDigits = Number.isInteger(fractionDigits) ? fractionDigits : 0;
    const maximumFractionDigits = Number.isInteger(fractionDigits) ? fractionDigits : 20;
    const absoluteValue = Math.abs(value);
    const formattedNumber = new Intl.NumberFormat("en-US", {
        useGrouping,
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(absoluteValue);

    let text = `${prefix}${formattedNumber}${suffix}`;

    if (value < 0) {
        text = negativeParens ? `(${text})` : `-${text}`;
    }

    return text;
};

const resolveColorRange = (value, ranges = []) => {
    if (typeof value !== "number") {
        return null;
    }

    const matchedRange = ranges.find(([min, max]) => {
        const lowerBoundOk = min === null || min === undefined || value >= Number(min);
        const upperBoundOk = max === null || max === undefined || value <= Number(max);

        return lowerBoundOk && upperBoundOk;
    });

    if (!matchedRange) {
        return null;
    }

    const [, , textColor, backgroundColor] = matchedRange;
    return {
        color: textColor || undefined,
        backgroundColor: backgroundColor || undefined,
    };
};

const formatCellValue = (value, formatters = []) => {
    const formatted = {
        rawValue: value,
        displayValue: value === null || value === undefined ? "" : String(value),
        textColor: undefined,
        backgroundColor: undefined,
    };

    formatters.forEach((formatter) => {
        if (formatter.type === "NumberFormat") {
            formatted.displayValue = formatNumberValue(value, formatter.options || {});
        }

        if (formatter.type === "ColorFormat") {
            const colorStyle = resolveColorRange(value, formatter.ranges || []);

            if (colorStyle) {
                formatted.textColor = colorStyle.color;
                formatted.backgroundColor = colorStyle.backgroundColor;
            }
        }
    });

    return formatted;
};

const buildValueItems = (row, headerRow, valueColumns, formatterMap) => {
    return valueColumns.map((columnIndex) => {
        const formatters = formatterMap[columnIndex] || [];
        const formatted = formatCellValue(row[columnIndex], formatters);

        return {
            columnIndex,
            label: headerRow[columnIndex],
            value: row[columnIndex],
            displayValue: formatted.displayValue,
            textColor: formatted.textColor,
            backgroundColor: formatted.backgroundColor,
            formatters,
        };
    });
};

const buildNodePath = (row, levelColumns, depth) => {
    return levelColumns.slice(0, depth + 1).map((columnIndex) => row[columnIndex]);
};

const cloneCardForTree = (card) => ({
    ...card,
    children: [],
});

const buildPyramidTree = (levels = [], totalCard = null, showTotalColumn = false) => {
    const treeLevels = levels.map((level) => ({
        ...level,
        cards: level.cards.map(cloneCardForTree),
    }));
    const cardMap = new Map();

    treeLevels.forEach((level) => {
        level.cards.forEach((card) => {
            cardMap.set(card.id, card);
        });
    });

    const rootChildren = [];

    treeLevels.forEach((level, levelIndex) => {
        level.cards.forEach((card) => {
            if (levelIndex === 0) {
                rootChildren.push(card);
                return;
            }

            const parentPath = card.path.slice(0, -1);
            const parentId = parentPath.join("__");
            const parentCard = cardMap.get(parentId);

            if (parentCard) {
                parentCard.children.push(card);
            }
        });
    });

    if (!showTotalColumn || !totalCard) {
        return {
            treeLevels,
            rootNode: null,
            rootChildren,
        };
    }

    return {
        treeLevels,
        rootNode: {
            ...cloneCardForTree(totalCard),
            id: "__total__",
            path: [],
            levelIndex: -1,
            children: rootChildren,
        },
        rootChildren,
    };
};

const buildPyramidModel = (chartData, chartOptions = {}, chartFormatters = []) => {
    if (!Array.isArray(chartData) || chartData.length < 2) {
        return null;
    }

    const headerRow = chartData[0];
    const dataRows = chartData.slice(1);
    const {
        orientation = "horizontal",
        parentAlignment = "top",
        showConnectors = true,
        showDetailTables = true,
        cardViewKey = "default",
        tableViewKey = "default",
        levelColumns = [],
        tableColumns = [],
        valueColumns = [],
        showTotalColumn = false,
    } = chartOptions;

    const formatterMap = buildFormatterMap(chartFormatters);
    const totalRow = showTotalColumn ? dataRows.find((row) => row[levelColumns[0]] === "Total") || null : null;

    const levels = levelColumns.map((columnIndex, levelIndex) => {
        const cards = dataRows
            .filter((row) => {
                if (showTotalColumn && row[levelColumns[0]] === "Total") {
                    return false;
                }

                const hasLevelValue = row[columnIndex] !== null && row[columnIndex] !== undefined;
                const isCurrentDepth = levelColumns
                    .slice(levelIndex + 1)
                    .every((nextColumnIndex) => row[nextColumnIndex] === null || row[nextColumnIndex] === undefined);
                const hasNoTableValue = tableColumns
                    .every((tableColumnIndex) => row[tableColumnIndex] === null || row[tableColumnIndex] === undefined);

                return hasLevelValue && isCurrentDepth && hasNoTableValue;
            })
            .map((row) => {
                const path = buildNodePath(row, levelColumns, levelIndex);
                const tableRows = showDetailTables
                    ? dataRows
                        .filter((candidateRow) => {
                            const samePath = levelColumns.every((pathColumnIndex, pathIndex) => {
                                if (pathIndex > levelIndex) {
                                    return true;
                                }

                                return candidateRow[pathColumnIndex] === row[pathColumnIndex];
                            });
                            const hasTableValue = tableColumns.some(
                                (tableColumnIndex) => candidateRow[tableColumnIndex] !== null && candidateRow[tableColumnIndex] !== undefined
                            );

                            return samePath && hasTableValue;
                        })
                        .map((candidateRow) => ({
                            sourceRow: candidateRow,
                            label: tableColumns.map((tableColumnIndex) => candidateRow[tableColumnIndex]).filter(Boolean).join(" / "),
                            values: buildValueItems(candidateRow, headerRow, valueColumns, formatterMap),
                        }))
                    : [];

                return {
                    id: path.join("__"),
                    levelIndex,
                    columnIndex,
                    label: row[columnIndex],
                    path,
                    sourceRow: row,
                    values: buildValueItems(row, headerRow, valueColumns, formatterMap),
                    tableRows,
                };
            });

        return {
            levelIndex,
            columnIndex,
            label: headerRow[columnIndex],
            cards,
        };
    });

    const tree = buildPyramidTree(levels, totalRow ? {
        label: totalRow[levelColumns[0]],
        sourceRow: totalRow,
        values: buildValueItems(totalRow, headerRow, valueColumns, formatterMap),
        tableRows: [],
    } : null, showTotalColumn);

    return {
        headers: headerRow,
        options: {
            orientation,
            parentAlignment,
            showConnectors,
            showDetailTables,
            cardViewKey,
            tableViewKey,
            levelColumns,
            tableColumns,
            valueColumns,
            showTotalColumn,
        },
        formatters: {
            raw: chartFormatters,
            byColumn: formatterMap,
        },
        totalCard: tree.rootNode,
        tree,
        levels,
    };
};

const buildNumberFormatter = (formatConfig = {}) => {
    const {
        decimals,
        thousands = false,
        fixedDecimals = false,
    } = formatConfig;

    const formatter = new Intl.NumberFormat("en-US", {
        useGrouping: thousands,
        minimumFractionDigits: Number.isInteger(decimals) && fixedDecimals ? decimals : 0,
        maximumFractionDigits: Number.isInteger(decimals) ? decimals : 20,
    });

    return (value) => formatter.format(value);
};

const normalizeAnnotationConfig = (annotationConfig, seriesCount) => {
    if (!annotationConfig) {
        return [];
    }

    if (annotationConfig === true) {
        return Array.from({ length: seriesCount }, (_, index) => index);
    }

    const enabledSeries = annotationConfig.series || annotationConfig.columns;

    if (Array.isArray(enabledSeries)) {
        return enabledSeries
            .map((index) => Number(index))
            .filter((index) => Number.isInteger(index) && index >= 0 && index < seriesCount);
    }

    if (Number.isInteger(Number(enabledSeries))) {
        const seriesIndex = Number(enabledSeries);
        return seriesIndex >= 0 && seriesIndex < seriesCount ? [seriesIndex] : [];
    }

    return [];
};

const formatAnnotationValue = (value, annotationConfig = {}) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const formatConfig = annotationConfig.format || {};
    const {
        formatter,
        hideZero = false,
        prefix = "",
        suffix = "",
    } = formatConfig;

    if (hideZero && Number(value) === 0) {
        return null;
    }

    if (typeof formatter === "function") {
        return formatter(value);
    }

    if (typeof value === "number") {
        const formatNumber = buildNumberFormatter(formatConfig);
        return `${prefix}${formatNumber(value)}${suffix}`;
    }

    return `${prefix}${value}${suffix}`;
};

const addChartAnnotations = (data, annotationConfig) => {
    if (!Array.isArray(data) || data.length === 0 || !annotationConfig) {
        return data;
    }

    const headerRow = data[0];
    const seriesCount = headerRow.length - 1;
    const enabledSeries = normalizeAnnotationConfig(annotationConfig, seriesCount);

    if (!enabledSeries.length) {
        return data;
    }

    return data.map((row, rowIndex) => {
        const annotatedRow = [];

        row.forEach((cell, columnIndex) => {
            annotatedRow.push(cell);

            if (columnIndex === 0 || !enabledSeries.includes(columnIndex - 1)) {
                return;
            }

            if (rowIndex === 0) {
                annotatedRow.push({ role: "annotation" });
                return;
            }

            annotatedRow.push(formatAnnotationValue(cell, annotationConfig));
        });

        return annotatedRow;
    });
};

    // Remove trailing rows with all null/empty values (except header)
const replaceTrailingZerosWithNull = function(data) {
    if (!Array.isArray(data) || data.length < 2) return data;
    const result = data.map(row => [...row]);
    const numSeries = result[0].length - 1; // Exclude label column

    for (let col = 1; col <= numSeries; col++) {
        // Find last non-zero, non-null value for this series
        let lastIdx = result.length - 1;
        while (
            lastIdx > 0 &&
            (result[lastIdx][col] === 0 || result[lastIdx][col] === null || result[lastIdx][col] === undefined || result[lastIdx][col] === "")
        ) {
            lastIdx--;
        }
        // Replace trailing zeros with null
        for (let i = lastIdx + 1; i < result.length; i++) {
            result[i][col] = null;
        }
    }
    return result;
}

const getChartCellValue = (cell) => {
    if (cell && typeof cell === "object" && Object.prototype.hasOwnProperty.call(cell, "v")) {
        return cell.v;
    }

    return cell;
}

const blankChartCellDisplay = (cell) => {
    if (cell && typeof cell === "object" && Object.prototype.hasOwnProperty.call(cell, "v")) {
        return {
            ...cell,
            f: "",
        };
    }

    return {
        v: cell,
        f: "",
    };
}

const setChartCellProperties = (cell, properties = {}) => {
    if (cell && typeof cell === "object" && Object.prototype.hasOwnProperty.call(cell, "v")) {
        const currentStyle = cell.p?.style ? `${cell.p.style};` : "";
        const nextStyle = properties.style ? `${currentStyle}${properties.style}` : cell.p?.style;
        const nextClassName = [cell.p?.className, properties.className].filter(Boolean).join(" ");

        return {
            ...cell,
            p: {
                ...(cell.p || {}),
                ...(nextStyle ? { style: nextStyle } : {}),
                ...(nextClassName ? { className: nextClassName } : {}),
            },
        };
    }

    return {
        v: cell,
        p: {
            ...(properties.style ? { style: properties.style } : {}),
            ...(properties.className ? { className: properties.className } : {}),
        },
    };
}

const normalizeCustomTableStyleRules = (formatters = []) => {
    if (!Array.isArray(formatters)) {
        return [];
    }

    return formatters
        .filter((formatter) => formatter?.type === "StyleFormat")
        .map((formatter) => {
            const options = formatter.options || {};

            return {
                column: Number(formatter.column),
                value: options.value,
                scope: options.scope || "row",
                style: options.style,
                className: options.className,
            };
        })
        .filter((rule) => (
            Number.isInteger(rule.column)
            && rule.column >= 0
            && (rule.style || rule.className)
        ));
}

const hideRepeatedTableColumnValues = function (chartData, columns = []) {
    if (!Array.isArray(chartData) || chartData.length < 2 || !Array.isArray(chartData[0]) || !Array.isArray(columns) || !columns.length) {
        return chartData;
    }

    const hiddenColumnSet = new Set(
        columns
            .map((column) => Number(column))
            .filter((column) => Number.isInteger(column) && column >= 0 && column < chartData[0].length)
    );

    if (!hiddenColumnSet.size) {
        return chartData;
    }

    return chartData.map((row, rowIndex) => {
        if (rowIndex === 0 || !Array.isArray(row)) {
            return row;
        }

        const previousRow = chartData[rowIndex - 1];

        return row.map((cell, columnIndex) => {
            if (!hiddenColumnSet.has(columnIndex) || !Array.isArray(previousRow)) {
                return cell;
            }

            return getChartCellValue(cell) === getChartCellValue(previousRow[columnIndex])
                ? blankChartCellDisplay(cell)
                : cell;
        });
    });
}

const applyCustomTableStyling = function (chartData, customFormatters = []) {
    if (!Array.isArray(chartData) || chartData.length < 2 || !Array.isArray(chartData[0])) {
        return chartData;
    }

    const styleRules = normalizeCustomTableStyleRules(customFormatters)
        .filter((rule) => rule.column < chartData[0].length);

    if (!styleRules.length) {
        return chartData;
    }

    return chartData.map((row, rowIndex) => {
        if (rowIndex === 0 || !Array.isArray(row)) {
            return row;
        }

        return styleRules.reduce((styledRow, rule) => {
            if (getChartCellValue(styledRow[rule.column]) !== rule.value) {
                return styledRow;
            }

            const properties = {
                style: rule.style,
                className: rule.className,
            };

            if (rule.scope === "cell") {
                return styledRow.map((cell, columnIndex) => (
                    columnIndex === rule.column
                        ? setChartCellProperties(cell, properties)
                        : cell
                ));
            }

            return styledRow.map((cell) => setChartCellProperties(cell, properties));
        }, row);
    });
}

const checkOptionAvailable = function (layoutData, keyToCheck) {

    if(!layoutData) return false;
    
    let optionKeys = Object.keys(layoutData);

    for (const optk of optionKeys) {
        if(optk === keyToCheck) return true;
    }
    
    return false;
}

const buildLayoutConfig = function (layoutString) {

    if(!layoutString) return {};
    
    const layoutObject = JSON.parse(layoutString);
    const gridLayouts = layoutObject.LAYOUTS || layoutObject;

    let layoutConfig = {};

    let configKeys = Object.keys(gridLayouts);

    for (const ck of configKeys) {
        let configValues = Object.values(gridLayouts[ck]);
        let configTransformed = [];

        for (var i = 0; i < configValues.length; i++) {
            configTransformed.push(
                {
                    i: configValues[i].INSIGHT_KEY, 
                    x: configValues[i].X_COORD, 
                    y: configValues[i].Y_COORD, 
                    w: configValues[i].WIDTH, 
                    h: configValues[i].HEIGHT
                });
        }

        layoutConfig[ck] = configTransformed;
    }
    
    // console.log("insight dashboard", layoutConfig);

    return {
        layouts: layoutConfig,
        config: layoutObject.CONFIG || {}
    };
}

const buildCalendarView = function (viewData) {

    if(!viewData) return {};

    var charData = viewData.ChartData

    console.log("calendar data before", viewData);

    for (var i = 1; i < charData.length; i++)
    {
        let dateString = charData[i][0];

        let parse = Date.parse(dateString);

        let date = new Date(parse);

        viewData.ChartData[i][0] = date;
    }

    console.log("calendar data after", viewData);
}

const buildProdNoWorkTableView = function (viewData) {

    if(!viewData) return {};

    var charData = viewData.ChartData

    console.log("table data before", viewData);

    for (var i = 1; i < charData.length; i++)
    {
        let data = charData[i][2];

        var formatedData; 

        if (data === -1) {
            formatedData = "Never Assigned";
        }
        else {
            formatedData = fn.convertMinutesToHHMM(data);
        }

        viewData.ChartData[i][2] = formatedData;
    }

    console.log("table data after", viewData);
}


export {addChartAnnotations, buildPyramidModel, formatCellValue, mergeChartOptions, replaceTrailingZerosWithNull,
    applyCustomTableStyling, hideRepeatedTableColumnValues, buildLayoutConfig, checkOptionAvailable, buildCalendarView, buildProdNoWorkTableView};
